// client/src/lib/authors.ts
//
// One source of truth for who writes Delphina's money pages and why they are
// credible (issue #458). The byline, the author bar, Article schema and the
// author page all read from here, so the credential wording is said once.
//
// Compliance (hard rule): nothing here may imply that the author gives
// regulated advice or that Delphina is FCA-authorised. Every surface that shows
// the credential also shows GUIDANCE_NOT_ADVICE.

export const SITE_URL = 'https://delphina.money';

export const GUIDANCE_NOT_ADVICE =
  'Delphina provides guidance, not regulated financial advice.';

export interface AuthorCredential {
  /** Full name of the qualification. */
  name: string;
  /** Post-nominal shown in bylines. */
  short: string;
  /** What kind of credential it is, for schema.org credentialCategory. */
  category: string;
  awardedBy: { name: string; url: string };
}

export interface Author {
  slug: string;
  name: string;
  /** Shown after the name in bylines. */
  role: string;
  jobTitle: string;
  /** Path of the author page. */
  path: string;
  avatar: string;
  bio: string;
  /** Plain statement of what the credential does and does not mean. */
  credentialNote?: string;
  credential?: AuthorCredential;
  sameAs: string[];
}

export const SYD_LAWRENCE: Author = {
  slug: 'syd-lawrence',
  name: 'Syd Lawrence',
  role: 'Founder of Delphina',
  jobTitle: 'Founder, Delphina',
  path: '/authors/syd-lawrence',
  avatar: '/images/syd-headshot.jpg',
  bio: 'Syd founded Delphina after getting fed up with being kept confused by the UK personal finance industry. He holds the Level 4 Diploma for Financial Advisers (DipFA) but does not practise as a regulated adviser: he builds tools and writes guides that give people financial clarity.',
  credentialNote:
    'Syd holds the Level 4 Diploma for Financial Advisers (DipFA), the qualification UK financial advisers need. He is not a practising regulated adviser, and nothing he writes for Delphina is personal financial advice.',
  credential: {
    name: 'Level 4 Diploma for Financial Advisers',
    short: 'DipFA',
    category: 'Professional qualification',
    awardedBy: { name: 'The London Institute of Banking & Finance', url: 'https://www.libf.ac.uk/' },
  },
  sameAs: ['https://www.linkedin.com/in/sydlawrence'],
};

const AUTHORS_BY_NAME: Record<string, Author> = {
  [SYD_LAWRENCE.name]: SYD_LAWRENCE,
};

/** The full record for a byline name, if we have one. */
export function authorByName(name: string | undefined): Author | undefined {
  return name ? AUTHORS_BY_NAME[name] : undefined;
}

/** schema.org Person for an author, with the credential when there is one. */
export function personSchema(author: Author): Record<string, unknown> {
  return {
    '@type': 'Person',
    name: author.name,
    url: `${SITE_URL}${author.path}`,
    jobTitle: author.jobTitle,
    image: `${SITE_URL}${author.avatar}`,
    description: author.bio,
    sameAs: author.sameAs,
    worksFor: { '@type': 'Organization', name: 'Delphina', url: SITE_URL },
    ...(author.credential && {
      hasCredential: {
        '@type': 'EducationalOccupationalCredential',
        name: author.credential.name,
        alternateName: author.credential.short,
        credentialCategory: author.credential.category,
        recognizedBy: {
          '@type': 'Organization',
          name: author.credential.awardedBy.name,
          url: author.credential.awardedBy.url,
        },
      },
    }),
  };
}

/** "Syd Lawrence, DipFA" */
export function bylineName(author: Author): string {
  return author.credential ? `${author.name}, ${author.credential.short}` : author.name;
}
